import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../Button';
import setUiMessage from '../../../Utilities/Tostify';
import { post } from '../../Auths/AuthToken';
import Select from 'react-select';
import { zodErrorResolver } from '../../../Utilities/Utilities';
import reportSchema from '../../../schema/reportSchema';

function AddPayorPopup({ close = () => null, handlePayorSave = () => null }) {
  const [allData, setAllData] = useState({});
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    checkbox: true,
    mapInsId: { value: '', label: '' },
    insType: { value: '', label: '' },
    companyName: '',
    address_1: '',
    address_2: '',
    city: '',
    state: { value: '63', label: 'WV' },
    zip: '',
    claim_inquiry_phone: '',
    claim_inquiry_email: '',
    claim_fax: '',
    credentialing_desk_phone: '',
    credentialing_desk_fax: '',
    credentialing_desk_email: '',
    credentialing_desk_representative_name: '',
    credentialing_desk_representative_email: '',
    credentialing_desk_representative_phone: '',
    addInfo: [],
    secAddInfo: [],
  });

  const [claimType, setClaimType] = useState({
    primaryClaim: {
      gateway: '',
      payerId: '',
      eleigPayerId: '',
      gatewaySentMethod: '',
      formType: '',
    },
    secondaryClaim: {
      gateway: '',
      payerId: '',
      eleigPayerId: '',
      gatewaySentMethod: '',
      formType: '',
    },
  });

  const validateForm = useCallback(() => {
    const formScheema = reportSchema.addPayorScheema();
    const validation = formScheema.safeParse({
      companyName: formData?.companyName || '',
    });

    if (!validation.success) {
      return validation.error.issues;
    }
    return '';
  }, [formData?.companyName]);

  const handlePrimaryClaimAdd = () => {
    if (!claimType.primaryClaim.payerId) {
      setUiMessage('err', 'Please select primary claim gateway');
      return;
    }
    if (formData.addInfo.length > 0) {
      const isExist = formData.addInfo.find(
        (item) => item.payerId === claimType.primaryClaim.payerId
      );
      if (isExist) {
        setUiMessage('err', 'This payer id already exist');
        return;
      }
    }

    setFormData({
      ...formData,
      addInfo: [
        ...formData.addInfo,
        {
          ...claimType.primaryClaim,
          gateway: claimType.primaryClaim.gateway,
          payerId: claimType.primaryClaim.payerId,
          eleigPayerId: claimType.primaryClaim.eleigPayerId,
          gatewaySentMethod: claimType.primaryClaim.gatewaySentMethod,
          formType: claimType.primaryClaim.formType,
        },
      ],
    });
  };

  const deletePrimaryGatewayInfo = (index) => {
    const newAddInfo = formData.addInfo.filter((item, i) => i !== index);
    setFormData({ ...formData, addInfo: newAddInfo });
  };

  const handleSecClaimAdd = () => {
    if (!claimType.secondaryClaim.payerId) {
      setUiMessage('err', 'Please select secondary claim gateway');
      return;
    }
    if (formData.secAddInfo.length > 0) {
      const isExist = formData.secAddInfo.find(
        (item) => item.payerId === claimType.secondaryClaim.payerId
      );
      if (isExist) {
        setUiMessage('err', 'This payer id already exist');
        return;
      }
    }
    setFormData({
      ...formData,
      secAddInfo: [
        ...formData.secAddInfo,
        {
          ...claimType.secondaryClaim,
          gateway: claimType.secondaryClaim.gateway,
          payerId: claimType.secondaryClaim.payerId,
          eleigPayerId: claimType.secondaryClaim.eleigPayerId,
          gatewaySentMethod: claimType.secondaryClaim.gatewaySentMethod,
          formType: claimType.secondaryClaim.formType,
        },
      ],
    });
  };

  const deleteSecGatewayInfo = (index) => {
    const newAddInfo = formData.secAddInfo.filter((item, i) => i !== index);
    setFormData({ ...formData, secAddInfo: newAddInfo });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await post(
          'api/reports/managePayerMatrix/addNewInsInMasters',
          {},
          false
        );
        if (response.status === 200) {
          setAllData({
            ...response.data,
            insuranceType: response.data.insuranceType.map((item) => ({
              value: item.id,
              label: item.title, // label is what is shown in the dropdown
            })),
            stateList: response.data.stateList.map((item) => ({
              value: item.id,
              label: item.iso_code, // label is what is shown in the dropdown
            })),
            mapInsList: response.data.mapInsList.map((item) => ({
              value: item.id,
              label: item.insurance_company_name,
            })),
          });
        }
      } catch (error) {
        setUiMessage('err', error?.message);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    const validationError = validateForm();
    setErrors({}); // Clear previous errors
    if (validationError) {
      setErrors(zodErrorResolver(validationError)); // Display validation errors
      return;
    }

    try {
      const response = await post(
        'api/reports/managePayerMatrix/saveNewInsInMasters',
        {
          companyName: formData.companyName,
          insurance_type_id: formData.insType?.value,
          address_1: formData.address_1,
          address_2: formData.address_2,
          city: formData.city,
          state: formData.state?.value,
          zip: formData.zip,
          claim_inquiry_phone: formData.claim_inquiry_phone,
          claim_inquiry_email: formData.claim_inquiry_email,
          claim_fax: formData.claim_fax,
          credentialing_desk_phone: formData.credentialing_desk_phone,
          credentialing_desk_fax: formData.credentialing_desk_fax,
          credentialing_desk_email: formData.credentialing_desk_email,
          credentialing_desk_representative_name:
            formData.credentialing_desk_representative_name,
          credentialing_desk_representative_email:
            formData.credentialing_desk_representative_email,
          credentialing_desk_representative_phone:
            formData.credentialing_desk_representative_phone,
          masterIns: formData.mapInsId?.value,
          source: '0',
          eraId: '',
          mode: '0',
          payerId: '0',
          addInfo: [
            ...(formData.addInfo.length > 0
              ? formData.addInfo.map((item) => {
                  return `${item.gateway?.value} | ${item.payerId} | ${item.eleigPayerId} | ${item.gatewaySentMethod?.value} | ${item.formType?.value}`;
                })
              : []),
          ],
          secAddInfo: [
            ...(formData.secAddInfo.length > 0
              ? formData.secAddInfo.map((item) => {
                  return `${item.gateway?.value} | ${item.payerId} | ${item.eleigPayerId} | ${item.gatewaySentMethod?.value} | ${item.formType?.value}`;
                })
              : []),
          ],
        },
        false
      );
      if (response.status === 200) {
        handlePayorSave(response.data?.mapInsList ?? [], {
          value: response.data?.insMasId,
          label: response.data?.insMasName,
        });
        setUiMessage('success', 'Payor added successfully');
        close();
      }
    } catch (error) {
      setUiMessage('err', error?.message);
    }
  };

  return (
    <div id="newInsInMastersPopup" className="modalBackground">
      <div className="add-payor-cntnr">
        <div className="add-payor-cntnr-hdr">
          <span className="clear-left10">Add Payor</span>
          <Button variant="circle" onClick={close}>
            X
          </Button>
        </div>
        <div className="add-payor-cntnr-bdy">
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">
              Payor Name<span className="red">*</span>:
            </span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="companyName"
                value={formData.companyName}
                onChange={(e) =>
                  setFormData({ ...formData, companyName: e.target.value })
                }
              />
              {errors?.companyName && (
                <p className="error">{errors?.companyName?.message}</p>
              )}
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Payor Type:</span>
            <span className="add-payor-val">
              <Select
                sel
                value={formData.insType}
                options={allData?.insuranceType}
                onChange={(option) =>
                  setFormData({ ...formData, insType: option })
                }
                className="react-select-addPayor-container"
                classNamePrefix="react-select"
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Address1:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="address_1"
                value={formData.address_1}
                onChange={(e) =>
                  setFormData({ ...formData, address_1: e.target.value })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Address2:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="address_2"
                value={formData.address_2}
                onChange={(e) =>
                  setFormData({ ...formData, address_2: e.target.value })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">City:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="city"
                value={formData.city}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">State:</span>
            <span className="add-payor-val">
              <Select
                value={formData.state}
                options={allData?.stateList}
                onChange={(option) =>
                  setFormData({ ...formData, state: option })
                }
                className="react-select-addPayor-container"
                classNamePrefix="react-select"
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Zip:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="zip"
                value={formData.zip}
                onChange={(e) =>
                  setFormData({ ...formData, zip: e.target.value })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Claim Inquiry Phone:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="claim_inquiry_phone"
                maxLength="14"
                size="12"
                value={formData.claim_inquiry_phone}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    claim_inquiry_phone: e.target.value,
                  })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Claim Inquiry email:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="claim_inquiry_email"
                value={formData.claim_inquiry_email}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    claim_inquiry_email: e.target.value,
                  })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Claim fax:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="claim_fax"
                maxLength="14"
                size="12"
                value={formData.claim_fax}
                onChange={(e) =>
                  setFormData({ ...formData, claim_fax: e.target.value })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Credentialing Desk phone:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="credentialing_desk_phone"
                maxLength="14"
                size="12"
                value={formData.credentialing_desk_phone}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentialing_desk_phone: e.target.value,
                  })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Credentialing Desk fax:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="credentialing_desk_fax"
                maxLength="14"
                size="12"
                value={formData.credentialing_desk_fax}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentialing_desk_fax: e.target.value,
                  })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Credentialing Desk email:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="credentialing_desk_email"
                value={formData.credentialing_desk_email}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentialing_desk_email: e.target.value,
                  })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">
              Credentialing Desk representative:
            </span>
            <span className="add-payor-val">&nbsp;</span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Name:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="credentialing_desk_representative_name"
                value={formData.credentialing_desk_representative_name}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentialing_desk_representative_name: e.target.value,
                  })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Email:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="credentialing_desk_representative_email"
                value={formData.credentialing_desk_representative_email}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentialing_desk_representative_email: e.target.value,
                  })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <span className="add-payor-lbl">Phone:</span>
            <span className="add-payor-val">
              <input
                type="text"
                className="payor-inpt"
                id="credentialing_desk_representative_phone"
                maxLength="14"
                size="12"
                value={formData.credentialing_desk_representative_phone}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    credentialing_desk_representative_phone: e.target.value,
                  })
                }
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw" id="mapMasterInsSec">
            <span className="add-payor-lbl">Map Insurance with:</span>
            <span className="add-payor-val">
              <Select
                value={formData.mapInsId}
                options={allData?.mapInsList}
                onChange={(option) =>
                  setFormData({ ...formData, mapInsId: option })
                }
                className="react-select-addPayor-container"
                classNamePrefix="react-select"
              />
            </span>
          </div>
          <div className="add-payor-cntnr-bdy-rw">
            <input
              type="checkbox"
              id="payerGatwaychecked"
              checked={formData.checkbox}
              onChange={(e) =>
                setFormData({ ...formData, checkbox: !formData.checkbox })
              }
            />
          </div>
          <div className={formData.checkbox ? 'show' : 'hide'}>
            <fieldset>
              <legend>Primary Claim:</legend>
              <div className="add-payor-cntnr-bdy-rw">
                <span className="add-payor-lbl">Gateway:</span>
                <span className="add-payor-val">
                  <Select
                    value={claimType.primaryClaim.gateway}
                    options={allData?.gatewayList}
                    onChange={(option) =>
                      setClaimType({
                        ...claimType,
                        primaryClaim: {
                          ...claimType.primaryClaim,
                          gateway: option,
                        },
                      })
                    }
                    className="react-select-addPayor-container"
                    classNamePrefix="react-select"
                  />
                </span>
              </div>

              <div className="add-payor-cntnr-bdy-rw">
                <span className="add-payor-lbl">Payer ID:</span>
                <span className="add-payor-val">
                  <input
                    type="text"
                    className="emp-inp-fld pms-ins-mstr-inp-fld"
                    name="payerId"
                    id="payerId"
                    maxLength="28"
                    value={claimType.primaryClaim.payerId}
                    onChange={(e) =>
                      setClaimType({
                        ...claimType,
                        primaryClaim: {
                          ...claimType.primaryClaim,
                          payerId: e.target.value,
                        },
                      })
                    }
                  />
                </span>
              </div>
              <div className="add-payor-cntnr-bdy-rw">
                <span className="add-payor-lbl">Eligibility Payer ID:</span>
                <span className="add-payor-val">
                  <input
                    type="text"
                    className="emp-inp-fld pms-ins-mstr-inp-fld"
                    name="eleigPayerId"
                    id="eleigPayerId"
                    maxLength="28"
                    value={claimType.primaryClaim.eleigPayerId}
                    onChange={(e) =>
                      setClaimType({
                        ...claimType,
                        primaryClaim: {
                          ...claimType.primaryClaim,
                          eleigPayerId: e.target.value,
                        },
                      })
                    }
                  />
                </span>
              </div>

              <div className="add-payor-cntnr-bdy-rw">
                <span className="add-payor-lbl">Method:</span>
                <span className="add-payor-val">
                  <Select
                    value={claimType.primaryClaim.gatewaySentMethod}
                    options={allData?.methodArr}
                    onChange={(option) =>
                      setClaimType({
                        ...claimType,
                        primaryClaim: {
                          ...claimType.primaryClaim,
                          gatewaySentMethod: option,
                        },
                      })
                    }
                    className="react-select-addPayor-container"
                    classNamePrefix="react-select"
                  />
                </span>
              </div>
              <div className="add-payor-cntnr-bdy-rw">
                <span className="add-payor-lbl">Form:</span>
                <span className="add-payor-val">
                  <Select
                    value={claimType.primaryClaim.formType}
                    options={allData?.formArr}
                    onChange={(option) =>
                      setClaimType({
                        ...claimType,
                        primaryClaim: {
                          ...claimType.primaryClaim,
                          formType: option,
                        },
                      })
                    }
                    className="react-select-addPayor-container"
                    classNamePrefix="react-select"
                  />
                </span>
              </div>
              <div
                className="add-payor-cntnr-bdy-rw"
                style={{ justifyContent: 'flex-end' }}
              >
                <Button variant="primary" onClick={handlePrimaryClaimAdd}>
                  Add
                </Button>
              </div>
              <div className="add-payor-cntnr-bdy-rw">
                <div id="addInfoParent" className="gatewayblock">
                  {formData.addInfo &&
                    formData.addInfo.map((item, index) => (
                      <div className="secAddInfoChild">
                        <div className="gatewayblock-row">
                          {`tryzeto | 12345678 | 123333 | Electronic | 5010`}
                        </div>
                        <div
                          className="left gatewayblock-del"
                          onClick={() => deletePrimaryGatewayInfo(index)}
                        >
                          X
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </fieldset>
            <fieldset>
              <legend>Secondary Claim:</legend>
              <div className="add-payor-cntnr-bdy-rw">
                <span className="add-payor-lbl">Gateway:</span>
                <span className="add-payor-val">
                  <Select
                    value={claimType.secondaryClaim.gateway}
                    options={allData?.gatewayList}
                    onChange={(option) =>
                      setClaimType({
                        ...claimType,
                        secondaryClaim: {
                          ...claimType.secondaryClaim,
                          gateway: option,
                        },
                      })
                    }
                    className="react-select-addPayor-container"
                    classNamePrefix="react-select"
                  />
                </span>
              </div>

              <div className="add-payor-cntnr-bdy-rw">
                <span className="add-payor-lbl">Payer ID:</span>
                <span className="add-payor-val">
                  <input
                    type="text"
                    className="emp-inp-fld pms-ins-mstr-inp-fld"
                    name="secPayerId"
                    id="secPayerId"
                    maxLength="28"
                    value={claimType.secondaryClaim.payerId}
                    onChange={(e) =>
                      setClaimType({
                        ...claimType,
                        secondaryClaim: {
                          ...claimType.secondaryClaim,
                          payerId: e.target.value,
                        },
                      })
                    }
                  />
                </span>
              </div>
              <div className="add-payor-cntnr-bdy-rw">
                <span className="add-payor-lbl">Eligibility Payer ID:</span>
                <span className="add-payor-val">
                  <input
                    type="text"
                    className="emp-inp-fld pms-ins-mstr-inp-fld"
                    value={claimType.secondaryClaim.eleigPayerId}
                    name="secEleigPayerId"
                    id="secEleigPayerId"
                    maxLength="28"
                    onChange={(e) =>
                      setClaimType({
                        ...claimType,
                        secondaryClaim: {
                          ...claimType.secondaryClaim,
                          eleigPayerId: e.target.value,
                        },
                      })
                    }
                  />
                </span>
              </div>

              <div className="add-payor-cntnr-bdy-rw">
                <span className="add-payor-lbl">Method:</span>
                <span className="add-payor-val">
                  <Select
                    value={claimType.secondaryClaim.gatewaySentMethod}
                    options={allData?.methodArr}
                    onChange={(option) =>
                      setClaimType({
                        ...claimType,
                        secondaryClaim: {
                          ...claimType.secondaryClaim,
                          gatewaySentMethod: option,
                        },
                      })
                    }
                    className="react-select-addPayor-container"
                    classNamePrefix="react-select"
                  />
                </span>
              </div>
              <div className="add-payor-cntnr-bdy-rw">
                <span className="add-payor-lbl">Form:</span>
                <span className="add-payor-val">
                  <Select
                    value={claimType.secondaryClaim.formType}
                    options={allData?.formArr}
                    onChange={(option) =>
                      setClaimType({
                        ...claimType,
                        secondaryClaim: {
                          ...claimType.secondaryClaim,
                          formType: option,
                        },
                      })
                    }
                    className="react-select-addPayor-container"
                    classNamePrefix="react-select"
                  />
                </span>
              </div>
              <div
                className="add-payor-cntnr-bdy-rw"
                style={{ justifyContent: 'flex-end' }}
              >
                <Button variant="primary" onClick={handleSecClaimAdd}>
                  Add
                </Button>
              </div>

              <div className="add-payor-cntnr-bdy-rw">
                <div id="secAddInfoParent" className="gatewayblock">
                  {formData.secAddInfo &&
                    formData.secAddInfo.map((item, index) => (
                      <div className="secAddInfoChild">
                        <div className="gatewayblock-row">
                          {`
                          ${item.gateway.label ? item.gateway.label : ''} |
                          ${item.payerId ? item.payerId : ''} |
                          ${item.eleigPayerId ? item.eleigPayerId : ''} |
                          ${
                            item.gatewaySentMethod?.label
                              ? item.gatewaySentMethod?.label
                              : ''
                          } |
                          ${item.formType?.label ? item.formType?.label : ''}`}
                        </div>
                        <div
                          className="left gatewayblock-del"
                          onClick={() => deleteSecGatewayInfo(index)}
                        >
                          X
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </fieldset>
          </div>
        </div>
        <div className="add-payor-footer">
          <Button
            variant="outline"
            onClick={close}
            style={{ marginRight: '10px' }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AddPayorPopup;
